import { useCallback, useContext } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import useModal from '@/hooks/common/useModal';
import useCurrentUser from '@/hooks/store/useCurrentUser';
import { AppContext } from '@/providers/ContextProvider';
import { accessTokenStorage } from '@/utils/AccessTokenStorage';

import { buildErrorResponse } from './useError';

export type AccessTokenResponse = {
  access_token: string;
};

const refreshAccessTokenReq = async (): Promise<
  AccessTokenResponse['access_token']
> => {
  const res = await fetch('/api/school/auth/token/refresh', {
    method: 'POST',
    headers: {
      'content-type': 'application/json;charset=UTF-8',
    },
    credentials: 'same-origin',
  }).catch(() => {
    throw buildErrorResponse({
      status: 500,
    });
  });

  if (!res.ok) {
    throw buildErrorResponse({
      status: res.status,
    });
  }
  const resBody = (await res.json()) as AccessTokenResponse;
  return resBody.access_token;
};

class RefreshAccessTokenPromiseCache {
  private cache: Promise<void> | null;

  constructor() {
    this.cache = null;
  }

  public get(): Promise<void> | null {
    return this.cache;
  }

  public set(promise: Promise<void>): void {
    this.cache = promise;
  }

  public clear(): void {
    this.cache = null;
  }
}

// Singleton
export const refreshAccessTokenPromiseCache =
  new RefreshAccessTokenPromiseCache();

interface UseAuthenticationReturn {
  login: (token: string) => void;
  refreshAccessToken: () => Promise<void>;
  clearUserData: () => void;
  isLoggedIn: boolean;
}

const useAuthentication = (): UseAuthenticationReturn => {
  const { isLoggedIn, setIsLoggedIn } = useContext(AppContext);
  const [, setCurrentUser] = useCurrentUser();
  const { hideModal } = useModal();
  const queryClient = useQueryClient();

  const login = useCallback(
    (token: string) => {
      accessTokenStorage.set(token);
      setIsLoggedIn(true);
    },
    [setIsLoggedIn],
  );

  const refreshAccessToken = useCallback<() => Promise<void>>(() => {
    if (refreshAccessTokenPromiseCache.get() === null) {
      refreshAccessTokenPromiseCache.set(
        refreshAccessTokenReq()
          .then((newToken) => {
            login(newToken);
          })
          .finally(() => {
            refreshAccessTokenPromiseCache.clear();
          }),
      );
    }
    return refreshAccessTokenPromiseCache.get() as Promise<void>;
  }, [login]);

  const clearUserData = useCallback(() => {
    setIsLoggedIn(false);
    hideModal();
    accessTokenStorage.clear();
    setCurrentUser(null);
    queryClient.clear();
  }, [hideModal, queryClient, setCurrentUser, setIsLoggedIn]);

  return {
    login,
    refreshAccessToken,
    clearUserData,
    isLoggedIn,
  };
};

export default useAuthentication;
